import ol = require("ol");
import Templates = require("../../models/defaults");
import Utils = require("../../utils");
import geo = require("../../utils/geo");
import Portal = require("portal-gis");

function strip(geom, test: (a, b) => boolean) {
    if (!geom.length) return;
    if (typeof geom[0] !== "number") {
        return geom.map(g => strip(g, test));
    }
    return geom.filter(test);
}

class Converter {

    static about = `
Feature conversion routines for featured defined in configuration and native openlayers features.
    `;

    constructor(public sourceSrs = "EPSG:4326", public targetSrs = "EPSG:4326") {
    }

    // ol3 -> normalized
    private fromNativeFeature(feature: ol.Feature) {
        /*
    geometry: any;
    geometryType?: string;
    attributes: Dictionary<any>;
    */
        let result = <Rhythm.GIS.FeatureQueryResultFeature>{
            _extensions_: Utils.getExtensionKey(feature, true),
            attributes: geo.getOl3FeatureData(feature)
        };

        Utils.extend(result, {
            olFeature: feature, // normalized feature can reference olFeature
            normalizedFeature: result // ol feature can reference normalized feature
        });

        let geom = <ol.geom.SimpleGeometry>feature.getGeometry();
        if (geom) {
            geom = <any>geom.clone(); // going to transform it, don't want any side-effects.

            if (geom instanceof ol.geom.Point) {
                result.geometryType = "point";
            } else if (geom instanceof ol.geom.LineString) {
                result.geometryType = "line";
            } else if (geom instanceof ol.geom.Polygon) {
                result.geometryType = "polygon";
            } else if (geom instanceof ol.geom.MultiPoint) {
                result.geometryType = "multipoint";
            } else if (geom instanceof ol.geom.MultiLineString) {
                result.geometryType = "multiline";
            } else if (geom instanceof ol.geom.MultiPolygon) {
                result.geometryType = "multipolygon";
            }

            geom.transform(this.sourceSrs, this.targetSrs);
            result.geometry = geom["getCoordinates"]();

            if (geom.stride > 2) {
                strip(result.geometry, (v, i) => 2 > i % geom.stride);
            }
        }

        return result;
    }

    // config (4326) -> normalized (the format returned from queries)
    fromConfigLayer(layer: Portal.MapLayerLeaf) {

        let options = Utils.asDictionary(layer.Options.Values);

        let result = <Rhythm.GIS.FeatureQueryResultLayer>{
            spatialReference: { wkid: "EPSG:4326" }
        };

        result.features = layer.Features.Features.filter(f => !!f.geom).map(f => {
            let feature = {
                attributes: Utils.asDictionary(f.Attributes.Values),
                geometry: JSON.parse(0 === f.geom.indexOf("[") ? f.geom : `[${f.geom}]`),
                geometryType: f.type
            };

            // bind the feature to the configuration to we can restore the original configured options
            Utils.extend(feature, {
                config: f
            });
            return feature;
        });

        // complexity creep!  need to eliminate this hack, which stores the configuration layers symbology as an extended property
        "symbology".split(",").forEach(key => (options[key]) && Utils.extend(result, { [key]: options[key] }));

        return result;
    }

    // normalized -> config (4326)
    toConfigLayer(inputLayer: Rhythm.GIS.FeatureQueryResultLayer) {
        let srs = this.sourceSrs;

        if (inputLayer.spatialReference) {
            srs = inputLayer.spatialReference.wkid + "";
        }

        if (0 > srs.indexOf("EPSG:")) {
            srs = "EPSG:" + srs;
        }

        let features = inputLayer.features;
        if (srs !== "EPSG:4326") {
            let converter = new Converter(srs, "EPSG:4326");
            // srs -> 4326
            let olFeatures = converter.toNativeFeatures(inputLayer);
            converter = new Converter("EPSG:4326", "EPSG:4326");
            // 4326 -> 4326
            features = converter.fromNativeFeatures(olFeatures).features;
        }

        let outputLayer = Templates.asLayerTemplate({
            _extensions_: Utils.getExtensionKey(inputLayer, true),
            id: inputLayer.displayFieldName
        });

        outputLayer.Features.Features = features.map(f => {
            let featureExt = Utils.extend(f);
            let feature = Utils.defaults({
                id: f.attributes["id"] || f.attributes["FID"], // TODO, see key-template
                text: f.attributes[inputLayer.displayFieldName],
                geom: JSON.stringify(f.geometry),
                type: f.geometryType
            }, Templates.mapFeatureTemplate);

            feature.Attributes.Values = Utils.asOptions(f.attributes);

            // move extended data into options
            let originalFeatureConfiguration = <Portal.FeatureLeaf>featureExt.config;
            let originalConfig = Utils.asDictionary(originalFeatureConfiguration ? originalFeatureConfiguration.Options.Values : []);
            // TODO: this is a complex piece of crap...what can be eliminated?  symbology is the only extended property we're turning into an option
            // but what if the feature already has a symbology option and it is not the same?  which one wins?  Need OVOTT
            "symbology".split(",")
                .filter(k => typeof originalConfig[k] === "undefined" && typeof featureExt[k] !== "undefined")
                .forEach(k => originalConfig[k] = featureExt[k]);
            feature.Options.Values = Utils.asOptions(originalConfig);

            return feature;
        });

        return outputLayer;
    }

    static toNativeGeom(f: Rhythm.GIS.FeatureQueryResultFeature) {
        let geom: ol.geom.SimpleGeometry;
        switch (f.geometryType) {
            case "point":
                geom = new ol.geom.Point(f.geometry);
                break;
            case "line":
                geom = new ol.geom.LineString(f.geometry);
                break;
            case "polygon":
                geom = new ol.geom.Polygon(f.geometry);
                break;
            case "multipoint":
                geom = new ol.geom.MultiPoint(f.geometry);
                break;
            case "multiline":
                geom = new ol.geom.MultiLineString(f.geometry);
                break;
            case "multipolygon":
                geom = new ol.geom.MultiPolygon(f.geometry);
                break;
            default:
                break;
        }
        return geom;
    }

    // normalized -> ol3
    toNativeFeatures(layer: Rhythm.GIS.FeatureQueryResultLayer) {
        let sourceSrs = this.sourceSrs;
        if (layer.spatialReference) {
            sourceSrs = (layer.spatialReference.wkid || 4326) + "";
            if (0 > sourceSrs.indexOf("EPSG:")) {
                sourceSrs = "EPSG:" + sourceSrs;
            }
        }

        let targetSrs = this.targetSrs;

        let result = layer.features.map(f => {
            let geom = Converter.toNativeGeom(f);
            if (!geom) {
                console.warn("no geometry found");
            } else {
                geom = <ol.geom.SimpleGeometry>geom.transform(sourceSrs, targetSrs);
            }

            let feature = new ol.Feature({
                geometry: geom,
                name: f.attributes[layer.displayFieldName]
            });

            // attributes become properties of the feature
            {
                feature.setProperties(f.attributes);
            }

            // features share common extension used to reference each other
            {
                Utils.extend(f, {
                    olFeature: feature,
                    feature: f
                });
                feature["_extensions_"] = Utils.getExtensionKey(f, true);
            }

            return feature;
        });

        return result;

    }

    // ol3 -> normalized
    fromNativeFeatures(features: ol.Feature[]) {
        let layer = <Rhythm.GIS.FeatureQueryResultLayer>{
            features: features.map(f => this.fromNativeFeature(f)),
            spatialReference: { wkid: <any>this.targetSrs }
        };
        return layer;
    }

}

export = Converter;
